import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../services/api";
import AdminNavbar from "../components/AdminNavbar";
import "./AdminProductDetails.css";

const toDisplayPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return Number(num.toFixed(2)).toString();
};

export default function AdminProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Admin Product Details | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Inspect listing details on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  useEffect(() => {
    const loadProductDetails = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await api.getAdminProduct(id);
        if (!data.success) {
          setProduct(null);
          setError(data.message || "Failed to load product details");
          return;
        }

        const nextProduct = data.data;
        setProduct(nextProduct || null);

        // Handle both old string format and new object format for images
        const imageUrl = Array.isArray(nextProduct?.images)
          ? nextProduct.images[0]?.url || nextProduct.images[0]
          : nextProduct?.image || "";

        setActiveImage(imageUrl || "");
      } catch (err) {
        setProduct(null);
        setError("Failed to load product details");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadProductDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="apd-page">
        <AdminNavbar />
        <div className="apd-container">
          <div className="apd-loading">
            <div className="apd-spinner"></div>
            <p>Loading product details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="apd-page">
        <AdminNavbar />
        <div className="apd-container">
          <div className="apd-error">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <h2>Product not found</h2>
            <p>
              {error ||
                "The product you're looking for doesn't exist or has been removed."}
            </p>
            <button
              className="apd-btn apd-btn-primary"
              onClick={() => navigate("/admin/products")}
            >
              Back to Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  const galleryImages = Array.isArray(product.images)
    ? product.images.map((img) => (typeof img === "string" ? img : img.url))
    : product.image
      ? [product.image]
      : [];

  const variants =
    Array.isArray(product.variants) && product.variants.length > 0
      ? product.variants
      : [{ name: product.type || "Default", price: product.price }];

  const discountRate = Math.min(
    100,
    Math.max(0, Number(product.discountRate || 0)),
  );

  const getDiscountedPrice = (price) => {
    const basePrice = Number(price || 0);
    return Math.round(basePrice * (1 - discountRate / 100) * 100) / 100;
  };

  return (
    <div className="apd-page">
      <AdminNavbar />

      <div className="apd-container">
        {/* Back Button */}
        <button
          className="apd-back-btn"
          onClick={() => navigate("/admin/products")}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Products
        </button>

        {/* Header */}
        <div className="apd-header">
          <div className="apd-header-content">
            <span className="apd-header-label">Product Details</span>
            <h1>{product.name}</h1>
          </div>
          {discountRate > 0 && (
            <div className="apd-discount-badge">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
              {toDisplayPercent(discountRate)}% OFF
            </div>
          )}
        </div>

        <div className="apd-content">
          {/* Left Column - Images */}
          <div className="apd-gallery-section">
            <div className="apd-card">
              <div className="apd-card-header">
                <h3>Product Images</h3>
                <span className="apd-count">
                  {galleryImages.length} image
                  {galleryImages.length !== 1 ? "s" : ""}
                </span>
              </div>

              {galleryImages.length > 0 ? (
                <>
                  <div className="apd-main-image">
                    <img
                      src={activeImage || galleryImages[0]}
                      alt={product.name}
                    />
                  </div>
                  {galleryImages.length > 1 && (
                    <div className="apd-thumbnails">
                      {galleryImages.map((image, index) => (
                        <button
                          key={`${image}-${index}`}
                          className={`apd-thumb ${activeImage === image ? "active" : ""}`}
                          onClick={() => setActiveImage(image)}
                        >
                          <img
                            src={image}
                            alt={`${product.name} ${index + 1}`}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="apd-no-image">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  <span>No images available</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="apd-details-section">
            {/* Seller Info */}
            <div className="apd-card">
              <div className="apd-card-header">
                <h3>Seller Information</h3>
              </div>
              <div className="apd-seller-info">
                <div className="apd-seller-avatar">
                  {(product.seller?.username || product.sellerName || "S")
                    .charAt(0)
                    .toUpperCase()}
                </div>
                <div className="apd-seller-details">
                  <span className="apd-seller-name">
                    {product.seller?.username ||
                      product.sellerName ||
                      "Unknown"}
                  </span>
                  <span className="apd-seller-label">Product Seller</span>
                </div>
              </div>
            </div>

            {/* Variants & Pricing */}
            <div className="apd-card">
              <div className="apd-card-header">
                <h3>Variants & Pricing</h3>
                <span className="apd-count">
                  {variants.length} variant{variants.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="apd-variants-list">
                {variants.map((variant, index) => (
                  <div
                    className="apd-variant-item"
                    key={`${variant.name || "default"}-${index}`}
                  >
                    <div className="apd-variant-type">
                      <span className="apd-variant-label">Name</span>
                      <span className="apd-variant-value">
                        {variant.name || `Variant ${index + 1}`}
                      </span>
                    </div>
                    <div className="apd-variant-prices">
                      <div className="apd-price-block">
                        <span className="apd-price-label">Original</span>
                        <span
                          className={`apd-price-value ${discountRate > 0 ? "striked" : ""}`}
                        >
                          BND {Number(variant.price || 0).toFixed(2)}
                        </span>
                      </div>
                      {discountRate > 0 && (
                        <div className="apd-price-block">
                          <span className="apd-price-label">Discounted</span>
                          <span className="apd-price-value discounted">
                            BND {getDiscountedPrice(variant.price).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="apd-card">
              <div className="apd-card-header">
                <h3>Categories</h3>
              </div>
              <div className="apd-categories">
                {Array.isArray(product.categories) &&
                product.categories.length > 0 ? (
                  product.categories.map((category) => (
                    <span key={category} className="apd-category-tag">
                      {category}
                    </span>
                  ))
                ) : (
                  <span className="apd-no-data">No categories assigned</span>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="apd-card">
              <div className="apd-card-header">
                <h3>Description</h3>
              </div>
              <p className="apd-description">
                {product.description?.trim() || "No description available."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
